import { motion } from "motion/react"
import type { ReactNode } from "react"
import { useEffect } from "react"

import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

import { HairlineRule } from "./jacket-chrome"
import { REDUCED_FADE } from "./motion"
import { PaperOutroSlate } from "./paper-outro-slate"
import type { OutroSlate } from "./paper-types"
import { bylineName, chromeLabel, jacketTitle } from "./type-voices"

export interface PaperOutroProps {
  playlistTitle: string
  creator?: string
  onReplay: () => void
  onClose?: () => void
  /** The server-resolved onward slate. When present (and non-empty) it takes
   *  the place of the `cta` slot as the outro's promoted step. */
  slate?: OutroSlate | null
  /** Fallback CTA — the outro's one yellow moment, shown only when the slate is
   *  empty (a playlist with no validated placements). */
  cta?: ReactNode
  reduce: boolean
  /** Widens the content column (desktop showcase — the Lab's browser frame).
   *  The player keeps the default reading-measure column; everything past
   *  max-w-md is container-query territory the phone never enters. */
  wide?: boolean
  /** Skips the slide-up entirely (the Lab preview, where the outro re-mounts
   *  on every control change and the entrance replay reads as noise). */
  disabledInitAnimation?: boolean
  disabledTriggerAutoPlay?: boolean
}

/**
 * The final plate in the shell's own paper language: dialog role, focus
 * target, replay + exit, and either the Onwards slate or the product CTA —
 * staying on the stack's material. A dark music-app outro would ship a banned
 * dark shell as the last thing a viewer sees. (Adjudicated in the written
 * notes; it replaced the retired feed's dark outro.)
 *
 * When the playlist has validated placements the slate drives the surface: one
 * promoted continuation card (the curator's next step) plus quieter exploration
 * rows, both rendered by the shared PaperCard. With no placements the Being
 * Herd `cta` is the empty-slate fallback.
 */
export function PaperOutro({
  playlistTitle,
  creator,
  onReplay,
  onClose,
  slate,
  cta,
  reduce,
  wide = false,
  disabledInitAnimation = false,
  disabledTriggerAutoPlay = false,
}: PaperOutroProps) {
  useEffect(() => {
    document
      .querySelector<HTMLButtonElement>("[data-outro-primary]")
      ?.focus({ preventScroll: true })
  }, [])

  const hasSlate =
    Boolean(slate?.continuation) ||
    Boolean(slate?.exploration && slate.exploration.length > 0)
  // Whether the slate or the cta owns the primary focus target; only when
  // neither does does "Back to the start" claim it.
  const primaryHandled = hasSlate || Boolean(cta)

  return (
    <motion.div
      role="dialog"
      aria-label="End of playlist"
      // `data-outro-scroll` attr is used for checking scroll pocition for trigger a backward gesture (wheel up / swipe down)
      data-outro-scroll
      initial={{ opacity: reduce ? 0 : 1, y: reduce ? 0 : "100vh" }}
      animate={{ opacity: 1, y: 0 }}
      className="pointer-events-auto absolute inset-0 z-40 flex items-center-safe overflow-y-auto px-6 py-8"
      exit={{ opacity: reduce ? 0 : 1, y: reduce ? 0 : "100vh" }}
      // Reduced motion: 200ms crossfade, no rise. disabledInitAnimation wins
      // over both: the Lab remounts this on every control change.
      transition={
        disabledInitAnimation
          ? { duration: 0 }
          : reduce
            ? REDUCED_FADE
            : { type: "spring", damping: 25, stiffness: 200 }
      }
    >
      <div
        className={cn(
          "@container mx-auto flex w-full flex-col items-start gap-6",
          wide ? "max-w-2xl" : "max-w-md",
        )}
      >
        <span className="text-batyr-ink/60" style={chromeLabel}>
          end of the stack
        </span>
        <div className="flex flex-col gap-2">
          <h2 style={jacketTitle}>{playlistTitle}</h2>
          {creator && <p style={bylineName}>{creator}</p>}
        </div>
        <HairlineRule className="w-14" />
        <p className="text-batyr-ink/60 linerNotes text-sm">
          {hasSlate
            ? "Every page in this stack was placed by a person. Here's where they'd point you next."
            : "Every page in this stack was placed by a person. Turn back through it any time."}
        </p>

        {hasSlate ? (
          <PaperOutroSlate
            slate={slate}
            disabledTriggerAutoPlay={disabledTriggerAutoPlay}
          />
        ) : (
          cta && <div className="flex w-full justify-center">{cta}</div>
        )}

        <div className="flex w-full flex-col items-center gap-3">
          {/* rounded-xs: the kit's 8px button radius reads app-card, not
              print — the feed's 0–4px rule wins on these surfaces. */}
          <Button
            type="button"
            color="secondary"
            size="lg"
            onClick={onReplay}
            className="w-full rounded-xs md:w-xs"
            {...(primaryHandled ? {} : { "data-outro-primary": true })}
          >
            Back to the start
          </Button>
          {onClose && (
            <Button
              type="button"
              color="tertiary"
              size="lg"
              onClick={onClose}
              className="w-full justify-center rounded-xs md:w-xs"
            >
              Exit player
            </Button>
          )}
        </div>
      </div>
    </motion.div>
  )
}
